import { Box, Paper, Stack, TablePagination } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from '../../../../components/common/app-table/ui';
import useDefaultTable from '../../../../components/common/app-table/useDefaultTable';
import FormAddButton from '../../../../components/common/FormAddButton';
import AppSearchField from '../../../../components/common/forms/AppSearchField';
import { ExportButton, GetTemplateButton } from '../../../../components/common/primebmd-ui/DataButtons';
import { ExportOperetorLicenses, IOperatorLicenses } from './utils/HEOperatorCertificateType-utils';
import HEOperatorLinceseTypeColumn from './HEOperatorCertificateTypeColumn';
import { ExportHEOperatorLicenseTypeToExcel, GetTemplateExportHEOperatorLicenseType } from './utils/exporting';
import { useGetOperatorsLicenseTypesQuery, useExportOperetorLicenseTypes, useImportOperatorLicenseTypes } from '../../../../hooks/useOperatorLicensesTypes';
import LoadingIndicator from '../../../../components/common/LoadingIndicator';
import { useSnackBar } from '../../../../components/ToastProvider';
import MultipleUploadButton from '../../../../components/common/MultipleUploadButton';
import * as xlsx from 'xlsx';
import { trimmedString } from '../../../../utils/trimmedString';
import { usePermission } from "../../../../hooks/usePermissions";
import { Modules, Permissions } from "../../../Users/Roles/roles-utils";
import { Navigate } from 'react-router-dom';

const OperatorLicenseTypeTable = () => {
  const hasPermission = usePermission(Modules.mmdOc.accessKey);
  const hasAddPermission = usePermission(
    Modules.mmdOc.accessKey,
    Permissions.add
  );
  const hasEditPermission = usePermission(
    Modules.mmdOc.accessKey,
    Permissions.edit
  );
  const hasDeletePermission = usePermission(
    Modules.mmdOc.accessKey,
    Permissions.delete
  );
  const hasImportPermission = usePermission(
    Modules.mmdOc.accessKey,
    Permissions.import
  );
  const hasExportPermission = usePermission(
    Modules.mmdOc.accessKey,
    Permissions.export
  );
  const { showSnackBar } = useSnackBar();

  const OperatorData = useGetOperatorsLicenseTypesQuery();
  const importData = useImportOperatorLicenseTypes();
  const data: IOperatorLicenses[] = OperatorData?.data === undefined ? [] : OperatorData.data;
  const formattedData: ExportOperetorLicenses[] = useExportOperetorLicenseTypes(data);


  const { table, dataCount, searchFilter, setSearchFilter, pageIndex, pageSize } = useDefaultTable<IOperatorLicenses>(
    data,
    HEOperatorLinceseTypeColumn()
  );

 
  
  const navigate = useNavigate();

  if (!hasPermission) return <Navigate to="/home" />;

  const emptyText = searchFilter ? 'No data found' : 'No data yet';

  const gotoAddOperator = () => navigate('/he-operator-certificate-type-new');

  const handleExport = () => {
    ExportHEOperatorLicenseTypeToExcel(formattedData, 'Operator License Type Database');
  }

  const handleTemplate = () => {
    GetTemplateExportHEOperatorLicenseType([], 'Template Operator License');
  
  }

  const readUploadFile = (e : any) => {
    e.preventDefault();
    if(e.target.files[0] !== undefined){
      const checkExcelName = e.target.files[0].name.split("_");
      if (checkExcelName[0].toUpperCase() === 'TEMPLATE OPERATOR LICENSE') {
          const reader = new FileReader();
          reader.onload = (e) => {
              const data = e.target?.result;
              const workbook = xlsx.read(data, { type: "array" });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const json = xlsx.utils.sheet_to_json(worksheet, { raw: false }); // Ensure dates are not treated as raw numbers
              const jsonData: any =  json === undefined ? [] : json;
              console.log(jsonData)
              const newData = jsonData.map((dataObject: any) => ({ 
                name: trimmedString(dataObject["Name"]),
                operatorType: trimmedString(dataObject["Operator Type"]),
                validity: Number(dataObject["Validity"]),
              }));

              if(newData.length !== 0){
                importData.mutate(newData);
              }
              else showSnackBar('No data found on excel', 'error');
          };
          reader.readAsArrayBuffer(e.target.files[0]);
      }
      else showSnackBar('Invalid file upload please download Excel template', 'error');
    }
    e.target.value = "";
}

  if (OperatorData?.isLoading) return <LoadingIndicator show />;

  return (
    <>
      <Stack direction='row'  justifyContent='flex-end' mb={2} gap={2}>
        {hasImportPermission && (
          <>
            <GetTemplateButton onClick={handleTemplate} />
            <MultipleUploadButton
              accept=".xlsx, .xls"
              onChange={readUploadFile}
            />
          </>
        )}
        {hasExportPermission && <ExportButton onClick={handleExport} />}
        <AppSearchField
          onSearchKeywordChange={setSearchFilter}
          searchKeyword={searchFilter ?? ''}
        />
        {hasAddPermission && <FormAddButton onClick={gotoAddOperator} />}
     
       
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align='center'>
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody dataCount={dataCount} loading={OperatorData?.isLoading} emptyText={emptyText}>
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align='center' style={{padding: (!hasEditPermission && !hasDeletePermission ? '12px': undefined)}}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position='relative'>
          <TablePagination
            sx={{ bgcolor: 'white', borderTop: '1px solid #eee' }}
            component='div'
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
      {OperatorData?.isError && showSnackBar('Network Error', 'error')}
    </>
  );
};

export default OperatorLicenseTypeTable;

